import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { plazaEspaña, arrayLinesNight, hourHiddenLinesNight } from '@app/constants';
import { Feature, RouteInterface, StopsInterface } from '@app/shared/models/stops.model';
import { HoursElement, PeriodicElement, Task } from '@app/shared/models/time.model';
import { BusService } from '@app/shared/services/bus.service';
import { RequestDestroyService } from '@app/shared/services/request-destroy.service';
import { environment } from '@environments/environment';
import moment, { duration } from 'moment-timezone';
import { NGXLogger } from 'ngx-logger';
import { Subject, timer } from 'rxjs';
import { first, switchMap, takeUntil, filter, tap } from 'rxjs/operators';
import { MapService } from '../map/map-services/map.service';
import { element } from 'protractor';
import {
  HttpTransportType,
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'ty-times',
  templateUrl: './times.component.html',
  styleUrls: ['./times.component.scss'],
})
export class TimesComponent implements OnInit, OnDestroy {
  buttonPressed: string[] = [];
  busId!: number;
  busname!: string;
  busCode!: string;
  stop!: number;
  stopName!: string;
  stopCode!: string;
  direction = 0;
  loadingSpinner = true;
  loadingSiriSpinner = true;
  displayedColumns: string[] = ['name'];
  displayedColumnsNow: string[] = ['minute', 'direction'];
  displayedColumnsDate: string[] = ['name'];
  dataSource: PeriodicElement[] = [];
  dateDataSource = new MatTableDataSource<PeriodicElement>();
  selected = new FormControl(0);
  activeTab = 0;
  todayTimes: PeriodicElement[] = [];
  listOfRoutes!: Task;
  firstTwentyStops!: HoursElement[];
  dateSelectedString!: string;
  changeDirectionSubscriber = false;
  changeDirectionDateSubscriber = false;
  hourInterval: any;
  notifier: Subject<void>;
  destroyTimer: Subject<void>;
  cleanDuplicateArray: { time: string; route_short_name: string; isLastStop: boolean }[];
  connection: signalR.HubConnection | undefined;
  arrived: boolean = false;
  entrada: boolean = false;

  isToday: boolean = true;
  estimatedTimes: any[] = [];

  constructor(
    private busService: BusService,
    private logger: NGXLogger,
    private mapService: MapService,
    private route: ActivatedRoute,
    private router: Router,
    private requestDestroyService: RequestDestroyService,
    private snackBar: MatSnackBar,
    private changeDet: ChangeDetectorRef
  ) {
    this.notifier = new Subject();
    this.destroyTimer = new Subject();
    this.cleanDuplicateArray = [];
  }

  ngOnInit(): void {
    console.log('ngOnInit');
    this.connection = new HubConnectionBuilder()
      .withUrl('https://localhost:44371/horarios', { transport: HttpTransportType.WebSockets })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Information)
      .build();

    this.route.queryParamMap.pipe(takeUntil(this.notifier)).subscribe(
      (queryParams) => {
        this.setParamsData();
        this.destroyTimer.next();
      },
      (error: Error) => {
        this.logger.error(`TimesComponent.ngOnInit=> ${error}`);
        this.snackBar.open('Error al cargar los horarios', 'Aceptar', { duration: 6000 });
        throw error;
      }
    );

    // DEV-2162 rafa@softoursistemas.com
    // LLamada para rellenar las estimaciones de tiempos
    this.busService
      .getListOfEstimates(this.stopCode)
      .pipe(first())
      .subscribe(
        (res: any[]) => {
          // Procesamos los datos de estimaciones
          this.estimatedTimes = res.map((lineInfo: any) => ({
            line: lineInfo.line,
            route: lineInfo.route,
            estimations: lineInfo.estimations.map((estimation: any) => ({
              vehicleId: estimation.vehicleId,
              minutesToArrival: estimation.minutesToArrival,
            })),
          }));
        },
        (err) => {
          this.logger.error(`Error al cargar las estimaciones de tiempos ${err.message}`);
          this.snackBar.open('Error al cargar las estimaciones de tiempos', 'Aceptar', {
            duration: 6000,
          });
        }
      );
  }

  ngOnDestroy(): void {
    this.destroyTimer.next();
    this.notifier.next();
    this.notifier.complete();
    if (this.connection && this.connection.state == HubConnectionState.Connected) {
      this.connection
        ?.send('Disconnect', 'horarios-localhost-1---' + this.stop + '-')
        .then(async () => {
          this.connection?.stop();
        });
    }
  }

  setParamsData(): void {
    this.busId = Number(this.route.snapshot.queryParamMap.get('bus'));
    this.busname = String(this.route.snapshot.queryParamMap.get('busName'));
    this.busCode = String(this.route.snapshot.queryParamMap.get('busCode'));
    this.stop = Number(this.route.snapshot.queryParamMap.get('stop'));
    this.stopName = String(this.route.snapshot.queryParamMap.get('stopName'));
    this.stopCode = String(this.route.snapshot.queryParamMap.get('stopCode'));
    this.direction = Number(this.route.snapshot.queryParamMap.get('dir'));
    this.getListOfRoutes(this.stop);
    this.getStopsFromBus(this.busId);
  }

  getListOfRoutes(stopId: number): void {
    this.listOfRoutes = {
      busName: 'ListOfRoutes',
      selected: false,
      busId: 0,
      color: 'primary',
      subtasks: [],
      route_color: 'white',
    };

    this.busService
      .getListOfRoutes(stopId)
      .pipe(first())
      .subscribe(
        (res: RouteInterface[]) => {
          res.forEach((element: RouteInterface) => {
            const task: Task = {
              busName: element.route_short_name,
              busId: Number(element.route_id),
              route_color: element.route_color,
              selected: !this.busId || this.busId === Number(element.route_id),
              color: 'warn',
            };
            this.listOfRoutes.subtasks?.push(task);
          });

          // When have a default selected routes, call the method
          this.getTimesFromToday(this.stop, this.direction);
        },
        (err) => {
          this.logger.error(`Error al cargar las paradas ${err.message}`);
          this.snackBar.open('Error al cargar las paradas', 'Aceptar', { duration: 6000 });
        }
      );
  }

  getArrayWithoutDuplicates(sortedArray: HoursElement[]): HoursElement[] {
    const result = sortedArray.reduce((unique: any, o: any) => {
      if (
        !unique.some(
          (obj: HoursElement) =>
            obj.direction !== o.direction &&
            obj.route_short_name === o.route_short_name &&
            obj.hour === o.hour &&
            obj.minute === o.minute
        )
      ) {
        unique.push(o);
      }
      return unique;
    }, []);
    return result;
  }

  getArrayWithoutDuplicatesTodayAndDate(sortedArray: PeriodicElement[]): PeriodicElement[] {
    sortedArray.forEach((element: PeriodicElement) => {
      /** Unique result */
      if (element.name) {
        const result = element.name.reduce((unique: any, o: any) => {
          if (
            !unique.some(
              (obj: { minute?: string; stop?: string; route_color?: string }) =>
                obj.stop === o.stop && obj.minute === o.minute
            )
          ) {
            unique.push(o);
          }
          return unique;
        }, []);
        element.name = result;
      }
    });

    return sortedArray;
  }

  getTimesFromToday(stop: number, direction: number): void {
    console.log('getTimesFromToday');
    const actualDate = moment.tz(environment.timezone).toDate();
    const actualHour = actualDate.getHours();
    const actualMinutes = actualDate.getMinutes();
    const topHours: HoursElement[] = [];

    let routesStringUrl = '';
    this.buttonPressed = [];
    if (this.listOfRoutes.subtasks) {
      this.listOfRoutes.subtasks.forEach((element) => {
        if (element.selected) {
          routesStringUrl += `&routes=${element.busName}`;
          if (!this.buttonPressed.includes(element.busName)) {
            this.buttonPressed.push(element.busName);
          }
        }
      });
    }
    // ! GetSiriTimes
    // this.getSiriTimes(stop, routesStringUrl);

    this.loadingSpinner = true;
    this.dataSource = [];
    this.todayTimes = [];
    console.log('Stop', stop, 'Direction', direction, 'routesStringUrl', routesStringUrl);
    this.busService
      .getTimesList(stop, direction, routesStringUrl)
      .pipe(
        first(),
        takeUntil(this.destroyTimer),
        tap((res) => {
          console.log('Datos recibidos antes del pipe:', res);
        })
      )
      .subscribe(
        (res) => {
          // console.log("Res: ",res);
          if (Object.keys(res).length > 0) {
            this.changeDirectionSubscriber = false;

            Object.keys(res).forEach((element) => {
              const data: PeriodicElement = {};
              data.position = Number(element);

              const minutesTimes: { minute?: string; stop?: string }[] = [];
              res[element].forEach((times: HoursElement) => {
                const time: {
                  minute?: string;
                  stop?: string;
                  route_color?: string;
                  departureBoardingActivity?: string;
                  direction_id?: string;
                  isCircular?: boolean;
                  isLastStop?: boolean;
                  monitored?: string;
                  order?: string;
                } = {};
                time.minute = times.minute;
                time.stop = times.route_short_name;
                time.route_color = times.route_color;
                minutesTimes.push(time);
              });
              data.name = minutesTimes;
              this.todayTimes.push(data);
            });

            // SHORT ARRAY FOR GET THE CORRECT HOUR FORMAT
            const sortedArray: PeriodicElement[] = this.todayTimes.sort((n1, n2) => {
              if (n1.position && n2.position) {
                if (n1.position > n2.position) {
                  return 1;
                }
                if (n1.position < n2.position) {
                  return -1;
                }
              }
              return 0;
            });

            this.dataSource = this.transformTimes(sortedArray);
            this.dataSource = this.cleanTimeDuplicates(this.dataSource);
            console.log('dataSource1', this.dataSource);
            this.loadingSpinner = false;

            if (this.dateSelectedString) {
              this.getTimeForDate(this.dateSelectedString);
            }
          } else {
            this.logger.debug(`Invalid direction, no data found`);
            this.dataSource = [];
            this.dateDataSource.data = [];
            this.loadingSpinner = false;
          }
        },
        (err) => {
          this.logger.error(`Error al cargar los horarios ${err.message}`);
          this.snackBar.open('Error al cargar los horarios', 'Aceptar', { duration: 6000 });
        }
      );
  }

  async getSiriTimes(stop: number, routesStringUrl: string): Promise<void> {
    let actualDate = moment.tz(environment.timezone).toDate();
    let actualHour = actualDate.getHours();
    let actualMinutes = actualDate.getMinutes();
    let topHours: HoursElement[] = [];

    if (!this.entrada) {
      await this.connection?.start().catch(async (err: any) => {
        console.error(err);
        this.snackBar.open('Error al obtener los datos, inténtalo más tarde  ', 'Aceptar');
        this.loadingSiriSpinner = false;
      });

      this.entrada = true;
    }

    this.connection?.send('AddToGroup', '1---' + stop + '-').catch((err: any) => console.log(err));

    this.connection?.onreconnected((conectionId) => {
      // TODO: ADDTOGROUP
      this.connection
        ?.send(conectionId!, '1---' + stop + '-')
        .catch((err: any) => console.log(err));
    });
    this.connection?.on('GetHorarios', async (param: any) => {
      // console.log("PARAM", param);
      var horariosFiltrados: any = {};
      // this.convertObjects(param);

      for (const key in param) {
        if (Object.prototype.hasOwnProperty.call(param, key)) {
          horariosFiltrados[key] = param[key].filter((horarios: any) =>
            this.buttonPressed.includes(horarios.route_short_name)
          );
        }
      }

      var res = Object.keys(horariosFiltrados).length > 0 ? horariosFiltrados : param;

      // && reset actualDate to update de minutes SOLUTION
      actualDate = moment.tz(environment.timezone).toDate();
      actualHour = actualDate.getHours();
      actualMinutes = actualDate.getMinutes();

      if (res && Object.keys(res).length > 0) {
        let aimedArrivalSort: HoursElement[] = [];
        Object.keys(res).forEach((element) => {
          const minutesTimes: { minute?: string; stop?: string }[] = [];
          res[element].forEach((times: HoursElement) => {
            const newHour: HoursElement = {
              aimedArrivalTime: times.aimedArrivalTime,
              aimedDepartureTime: times.aimedDepartureTime,
              departureBoardingActivity: times.departureBoardingActivity,
              direction: times.direction,
              direction_id: times.direction_id,
              earliestExpectedDepartureTime: times.earliestExpectedDepartureTime,
              expectedArrivalTime: times.expectedArrivalTime,
              expectedDepartureTime: times.expectedDepartureTime,
              isCircular: times.isCircular,
              isLastStop: times.isLastStop,
              monitored: times.monitored,
              order: times.order,
              route_color: times.route_color,
              route_short_name: times.route_short_name,
              route_text_color: times.route_text_color,
              time: times.time,
              minute: times.minute,
              hour: times.hour,
              blockRef: times.blockRef,
              hiddenLastStop: false,
            };
            const time: {
              minute?: string;
              stop?: string;
              route_color?: string;
              time?: string;
              monitored?: string;
            } = {};
            time.minute = newHour.minute;
            time.stop = newHour.route_short_name;
            time.route_color = newHour.route_color;
            time.time = newHour.time;
            time.monitored = newHour.monitored;
            minutesTimes.push(time);
            const myMoment = moment.tz(time.time, environment.timezone);
            newHour.hour = myMoment.hour().toString();
            newHour.minute = ('0' + myMoment.minute().toString()).slice(-2);
            const timeDiff = moment.duration(myMoment.diff(actualDate));
            if (timeDiff.asHours() >= 0 && timeDiff.asMinutes() >= 0) {
              // duration in hours
              const hours = Math.abs(parseInt(timeDiff.asHours().toString(), 10));
              newHour.restHour = hours;
              // duration in minutes
              const minutes = Math.abs(parseInt(timeDiff.asMinutes().toString(), 10));
              newHour.restMinute = minutes % 60;
              if (myMoment.hour() === actualHour) {
                if (actualMinutes <= Number(times.minute)) {
                  topHours.push(newHour);
                }
              } else {
                topHours.push(newHour);
              }
            }
          });
        });

        // SHORT TOP 20 - HOUR
        const sortedArrayNow: HoursElement[] = topHours.sort((n1, n2) => {
          const n1Time = moment(n1.time);
          const n2Time = moment(n2.time);
          if (n1Time.isAfter(n2Time)) {
            return 1;
          } else {
            return -1;
          }
        });
        this.firstTwentyStops = [];
        this.firstTwentyStops = sortedArrayNow;
        this.cleanDuplicateArray = [];
        this.firstTwentyStops = this.firstTwentyStops.filter(
          (sortedStop) =>
            sortedStop.hiddenLastStop === false &&
            this.cleanDuplicate({
              time: sortedStop.time as string,
              route_short_name: sortedStop.route_short_name as string,
              isLastStop: sortedStop.isLastStop as boolean,
            })
        );
        topHours = [];
        this.loadingSiriSpinner = false;
      } else {
        this.firstTwentyStops = [];
        this.loadingSiriSpinner = false;
      }

      this.changeDet.detectChanges();
    });
  }
  cleanDuplicate({
    time,
    route_short_name,
    isLastStop,
  }: {
    time: string;
    route_short_name: string;
    isLastStop: boolean;
  }): boolean {
    if (
      this.cleanDuplicateArray.find(
        (clean: { time: any; route_short_name: any; isLastStop: any }) =>
          clean.time === time &&
          clean.route_short_name === route_short_name &&
          clean.isLastStop === isLastStop
      )
    ) {
      return false;
    } else {
      this.cleanDuplicateArray.push({ time, route_short_name, isLastStop });
      return true;
    }
  }

  getNewStopsTimes(): void {
    console.log('getNewStopsTimes');
    this.destroyTimer.next();
    this.connection?.send('Disconnect', 'horarios-localhost-1---' + this.stop + '-');
    this.getTimesFromToday(this.stop, this.direction);
    // console.log("Stop, direction", this.stop+"/"+this.direction);
    if (this.dateSelectedString) {
      this.getTimeForDate(this.dateSelectedString);
    }
  }

  changeStopTimes(): void {
    console.log('changeStopTimes');
    this.destroyTimer.next();
    this.getNewStopsTimes();
  }

  backClicked(): void {
    if (this.connection) {
    }
    this.requestDestroyService.destroy$.next();
    if (this.router.url.includes(this.busService.simulatePath)) {
      if (this.busId && this.busname && this.busCode) {
        this.router.navigate([this.busService.simulatePath, 'stops'], {
          queryParams: {
            bus: this.busId,
            busCode: this.busCode,
            busName: this.busname,
            dir: this.direction,
          },
        });
      } else {
        this.router.navigate([this.busService.simulatePath], {});
      }
    } else {
      if (this.busId && this.busname && this.busCode) {
        this.router.navigate(['/stops'], {
          queryParams: {
            bus: this.busId,
            busCode: this.busCode,
            busName: this.busname,
            dir: this.direction,
          },
        });
      } else {
        this.router.navigate(['/'], {});
      }
    }
  }

  getStopsFromBus(busId: number): void {
    if (busId > 0) {
      this.busService
        .getStopsList(busId, this.direction)
        .pipe(first())
        .subscribe(
          (res: any) => {
            this.sendBusStops(res);
          },
          (err: { message: any }) => {
            this.logger.error(`Error al cargar las paradas ${err.message}`);
            this.snackBar.open('Error al cargar las paradas', 'Ok', { duration: 4000 });
          }
        );
    } else {
      const lat = String(this.route.snapshot.queryParamMap.get('lat'));
      const lon = String(this.route.snapshot.queryParamMap.get('lon'));
      if (lat && lon) {
        this.mapService.setUbicationPoint([lon, lat]);
      }
    }
  }

  sendBusStops(stopsList: StopsInterface): void {
    stopsList.features.forEach((stops: Feature) => {
      if (this.stop === Number(stops.properties.stop_id)) {
        this.mapService.setUbicationPoint(stops.geometry.coordinates);
      }
    });
  }

  dateSelectEvent(event: MatDatepickerInputEvent<Date>): void {
    let tempDate = '';
    if (event) {
      tempDate = moment(event.value).format('YYYYMMDD');
      this.dateSelectedString = tempDate;
      console.log('dateSelectEvent', tempDate);
      this.getTimeForDate(tempDate);
    }
  }

  getTimeForDate(tempDate: string): void {
    console.log('getTimeForDate', tempDate);
    const todayTimes: PeriodicElement[] = [];
    let routesStringUrl = '';
    if (this.listOfRoutes.subtasks) {
      this.listOfRoutes.subtasks.forEach((element: { selected: any; busName: any }) => {
        if (element.selected) {
          // routesStringUrl += `&routes=${element.busName}`;
          routesStringUrl += `&routes=${element.busName}`;
        }
      });
      console.log('routesStringUrl', routesStringUrl);
    }

    const correctDirection: number = this.changeDirectionDateSubscriber
      ? this.direction > 0
        ? 0
        : 1
      : this.direction;

    this.loadingSpinner = true;
    this.busService
      .getTimesListWithDate(this.stop, correctDirection, routesStringUrl, tempDate)
      .pipe(first())
      .subscribe(
        (res: { [x: string]: HoursElement[] }) => {
          if (Object.keys(res).length > 0) {
            this.changeDirectionDateSubscriber = false;
            Object.keys(res).forEach((element) => {
              const data: PeriodicElement = {};
              data.position = Number(element);

              const minutesTimes: { minute?: string; stop?: string }[] = [];
              res[element].forEach((times: HoursElement) => {
                const time: { minute?: string; stop?: string; route_color?: string } = {};
                time.minute = times.minute;
                time.stop = times.route_short_name;
                time.route_color = times.route_color;
                minutesTimes.push(time);
              });
              data.name = minutesTimes;
              todayTimes.push(data);
            });

            // SHORT ARRAY FOR GET THE CORRECT HOUR FORMAT
            const sortedArray: PeriodicElement[] = todayTimes.sort((n1, n2) => {
              if (n1.position && n2.position) {
                if (n1.position > n2.position) {
                  return 1;
                }

                if (n1.position < n2.position) {
                  return -1;
                }
              }
              return 0;
            });

            this.dateDataSource.data = this.transformTimes(sortedArray);
            this.dateDataSource.data = this.cleanTimeDuplicates(this.dateDataSource.data);
            this.loadingSpinner = false;
          } else {
            if (!this.changeDirectionDateSubscriber) {
              this.logger.debug(`Invalid direction in Date selector, changing direction to:
            ${this.direction === 0 ? 1 : 0}`);
              this.changeDirectionDateSubscriber = true;
              this.getTimeForDate(tempDate);
            } else {
              this.logger.debug(`No results in the two directions`);
              this.dateDataSource.data = [];
              this.loadingSpinner = false;
            }
          }
        },
        (err: { message: any }) => {
          this.logger.error(`Error al cargar los horarios ${err.message}`);
          this.snackBar.open('Error al cargar las tarifas, prueba en unos minutos', 'Aceptar', {
            duration: 6000,
          });
        }
      );
  }
  cleanTimeDuplicates(data: PeriodicElement[]) {
    return data.reduce((prev: PeriodicElement[], curr) => {
      if (curr.name) {
        const stringify = curr.name.map((x) => JSON.stringify(x));
        curr.name = [...new Set(stringify)].map((x) => JSON.parse(x));
        return [...prev, curr];
      }

      return prev;
    }, []);
  }

  transformTimes(data: PeriodicElement[]) {
    const routesSelected = this.listOfRoutes.subtasks
      ?.filter((x: { selected: any }) => x.selected)
      ?.map((x: { busName: any }) => x.busName);

    return data
      .filter((x) => {
        for (const night of arrayLinesNight) {
          const isSelected = routesSelected?.includes(night) && x.name && x.name[0].stop === night;
          if (isSelected) {
            return x.position && x.position > hourHiddenLinesNight;
          }
        }
        return true;
      })
      .map((elem) => ({
        ...elem,
        position: elem.position && elem.position < 24 ? elem.position : (elem.position || 24) - 24,
      }));
  }

  refreshEstimations() {
    // DEV-2162 rafa@softoursistemas.com
    // LLamada para rellenar las estimaciones de tiempos
    this.busService
      .getListOfEstimates(this.stopCode)
      .pipe(first())
      .subscribe(
        (res: any[]) => {
          // Procesamos los datos de estimaciones
          this.estimatedTimes = res.map((lineInfo: any) => ({
            line: lineInfo.line,
            route: lineInfo.route,
            estimations: lineInfo.estimations.map((estimation: any) => ({
              vehicleId: estimation.vehicleId,
              minutesToArrival: estimation.minutesToArrival,
            })),
          }));
        },
        (err) => {
          this.logger.error(`Error al cargar las estimaciones de tiempos ${err.message}`);
          this.snackBar.open('Error al cargar las estimaciones de tiempos', 'Aceptar', {
            duration: 6000,
          });
        }
      );
  }
}
